// Task due date/time helpers. `due_date` is always the "YYYY-MM-DD" day;
// an optional `due_time_at` (UTC ISO) makes it a specific moment.
import { parseLocalDate } from "./dateLocal";

const HOUR = 3600000;

export const taskDueMoment = (t) => {
  if (t?.due_time_at) {
    const d = new Date(t.due_time_at);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = t?.due_date || "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const taskDueDay = (t) => {
  const s = (t?.due_date || "").slice(0, 10);
  const d = s ? parseLocalDate(s) : null;
  return d && !isNaN(d.getTime()) ? d : null;
};

const fmtDay = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const GENERIC_TZ = {
  "America/New_York": "ET", "America/Detroit": "ET", "America/Chicago": "CT", "America/Denver": "MT",
  "America/Phoenix": "MT", "America/Los_Angeles": "PT", "America/Anchorage": "AKT", "Pacific/Honolulu": "HT",
};

const viewerTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Short label for a timezone at a moment: "ET", "PT", else Intl's ("UTC", "GMT+5:30").
export const tzLabel = (tz, at = new Date()) => {
  if (GENERIC_TZ[tz]) return GENERIC_TZ[tz];
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value || "";
  } catch { return ""; }
};

const fmtTime = (m, tz) => {
  try { return m.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) }); }
  catch { return null; }
};

// { day, time, tz, leadTime } — leadTime ("11:30 AM PT") only when the lead's
// clock differs from the viewer's. null when undated.
export const taskDueParts = (t) => {
  const m = taskDueMoment(t);
  if (m) {
    const time = fmtTime(m);
    let leadTime = null;
    if (t.lead_tz) {
      const lt = fmtTime(m, t.lead_tz);
      if (lt && lt !== time) leadTime = `${lt} ${tzLabel(t.lead_tz, m)}`.trim();
    }
    return { day: fmtDay(m), time, tz: tzLabel(viewerTz(), m), leadTime };
  }
  const d = taskDueDay(t);
  return d ? { day: fmtDay(d), time: null } : null;
};

export const formatTaskDue = (t) => {
  const p = taskDueParts(t);
  if (!p) return "No date";
  if (!p.time) return `${p.day} (Anytime)`;
  return `${p.day} at ${p.time}${p.tz ? ` ${p.tz}` : ""}${p.leadTime ? ` (lead ${p.leadTime})` : ""}`;
};

// Edit-form values in the viewer's local time: { due_date, due_time }.
export const taskDueEditFields = (t) => {
  const m = taskDueMoment(t);
  if (!m) return { due_date: (t?.due_date || "").slice(0, 10), due_time: "" };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    due_date: `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(m.getDate())}`,
    due_time: `${pad(m.getHours())}:${pad(m.getMinutes())}`,
  };
};

// Timed: overdue 1h after its time. Anytime: overdue once its day has ended.
export const isTaskOverdue = (t, now = Date.now()) => {
  if (!t || t.status === "completed" || t.done) return false;
  const m = taskDueMoment(t);
  if (m) return now > m.getTime() + HOUR;
  const d = taskDueDay(t);
  if (!d) return false;
  const end = new Date(d);
  end.setDate(end.getDate() + 1);
  return now >= end.getTime();
};

// Date asc; on the same date timed tasks first (earliest → latest), then Anytime.
export const compareTaskDue = (a, b) => {
  const da = (a?.due_date || "").slice(0, 10) || "9999-12-31";
  const db = (b?.due_date || "").slice(0, 10) || "9999-12-31";
  if (da !== db) return da < db ? -1 : 1;
  const ma = taskDueMoment(a);
  const mb = taskDueMoment(b);
  if (ma && mb) return ma - mb;
  if (ma) return -1;
  if (mb) return 1;
  return 0;
};

// Payload fields for an optional time on a "YYYY-MM-DD" date.
export const buildDueTimeFields = (date, time) => {
  if (!date || !time) return { due_time_at: null, due_tz: null };
  const d = new Date(`${date}T${time}`);
  if (isNaN(d.getTime())) return { due_time_at: null, due_tz: null };
  return { due_time_at: d.toISOString(), due_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null };
};
